/**
 * Admin-side access to agencies and subscriptions.
 *
 * Reads go straight to Firestore (the rules let admins read these
 * collections). Every change goes through the same Cloud Functions the app
 * uses — adminReviewAgency, updateSubscription, adminRunSubscriptionScheduler
 * — so status transitions, wallet releases and notifications happen in one
 * place whoever triggers them.
 */
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

export type AgencyStatus = 'pending' | 'approved' | 'rejected' | 'suspended';

export interface AdminAgency {
  id: string;
  name: string;
  ownerUid: string;
  ownerName: string;
  phone: string;
  email?: string;
  address: string;
  about?: string;
  cityId?: string | null;
  cityName?: string | null;
  segmentNames: string[];
  verificationStatus: AgencyStatus;
  isActive: boolean;
  rejectionReason?: string | null;
  suspensionReason?: string | null;
  staffCount?: number;
  activeSubscriptions?: number;
  totalJobs?: number;
  totalEarnings?: number;
  createdAt?: Date;
  submittedAt?: Date;
}

export interface AgencyKyb {
  gstNumber?: string | null;
  registrationNumber?: string | null;
  bankAccountNumber?: string;
  ifscCode?: string;
  accountHolderName?: string;
}

export interface AgencyMemberRow {
  uid: string;
  name: string;
  phone?: string;
  role: 'owner' | 'manager' | 'staff';
  status: 'active' | 'removed';
}

export interface AdminSubscription {
  id: string;
  userId: string;
  customerInfo?: { name?: string; phone?: string | null };
  serviceName: string;
  segmentName?: string | null;
  pricePerVisit: number;
  schedule: { frequency: string; daysOfWeek?: number[]; startTime: string; durationMinutes: number };
  scheduleLabel?: string;
  startDate: string;
  endDate?: string | null;
  location?: { address?: { fullAddress?: string; area?: string; city?: string } };
  cityId?: string | null;
  status: 'requested' | 'active' | 'paused' | 'cancelled' | 'declined';
  provider?: { type: 'agency' | 'ninja'; name: string; agencyId?: string; ninjaId?: string } | null;
  assigneeName?: string;
  pause?: { from: string; until: string } | null;
  skipDates?: string[];
  visitsCompleted?: number;
  issue?: { date: string; reason: string } | null;
  createdAt?: Date;
}

const toDate = (v: any): Date | undefined => (v?.toDate ? v.toDate() : undefined);

const reviewFn = httpsCallable<{ agencyId: string; decision: string; reason?: string }, { status: string }>(functions, 'adminReviewAgency');
const updateSubFn = httpsCallable<Record<string, unknown>, { ok: boolean; visitsReleased?: number }>(functions, 'updateSubscription');
const schedulerFn = httpsCallable<{ date?: string }, { target: string; results: Record<string, number>; missedClosed: number }>(functions, 'adminRunSubscriptionScheduler');

/** Message the Functions error carries, for showing to the admin. */
export function callableError(e: any): string {
  const msg = typeof e?.message === 'string' ? e.message : '';
  return msg && msg !== 'internal' ? msg.replace(/^FirebaseError:\s*/, '') : 'Something went wrong';
}

class AgencyAdminService {
  /** Agencies, newest first. A city admin sees only their city's. */
  async getAgencies(cityId: string | null): Promise<AdminAgency[]> {
    const base = collection(db, 'agencies');
    const q = cityId
      ? query(base, where('cityId', '==', cityId), orderBy('createdAt', 'desc'), limit(500))
      : query(base, orderBy('createdAt', 'desc'), limit(500));
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const x = d.data();
      return {
        id: d.id,
        ...x,
        segmentNames: x.segmentNames || [],
        createdAt: toDate(x.createdAt),
        submittedAt: toDate(x.submittedAt),
      } as AdminAgency;
    });
  }

  async getKyb(agencyId: string): Promise<AgencyKyb | null> {
    const s = await getDoc(doc(db, 'agencies', agencyId, 'private', 'kyb'));
    return s.exists() ? (s.data() as AgencyKyb) : null;
  }

  async getMembers(agencyId: string): Promise<AgencyMemberRow[]> {
    const s = await getDocs(collection(db, 'agencies', agencyId, 'members'));
    const rank = { owner: 0, manager: 1, staff: 2 } as const;
    return s.docs
      .map((d) => ({ uid: d.id, ...(d.data() as any) }) as AgencyMemberRow)
      .sort((a, b) => rank[a.role] - rank[b.role] || a.name.localeCompare(b.name));
  }

  review(agencyId: string, decision: 'approve' | 'reject' | 'suspend' | 'reactivate', reason?: string) {
    return reviewFn({ agencyId, decision, reason }).then((r) => r.data);
  }

  /** Subscriptions, newest first. A city admin sees only their city's. */
  async getSubscriptions(cityId: string | null): Promise<AdminSubscription[]> {
    const base = collection(db, 'subscriptions');
    const q = cityId
      ? query(base, where('cityId', '==', cityId), limit(500))
      : query(base, orderBy('createdAt', 'desc'), limit(500));
    const snap = await getDocs(q);
    const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any), createdAt: toDate(d.get('createdAt')) }) as AdminSubscription);
    return rows.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
  }

  async getAgencySubscriptions(agencyId: string): Promise<AdminSubscription[]> {
    const snap = await getDocs(query(collection(db, 'subscriptions'), where('providerAgencyId', '==', agencyId), orderBy('createdAt', 'desc'), limit(200)));
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any), createdAt: toDate(d.get('createdAt')) }) as AdminSubscription);
  }

  cancelSubscription(subscriptionId: string, reason: string) {
    return updateSubFn({ subscriptionId, action: 'cancel', reason }).then((r) => r.data);
  }

  /** Super-admin: generate visits for a date now (defaults to tomorrow). */
  runScheduler(date?: string) {
    return schedulerFn(date ? { date } : {}).then((r) => r.data);
  }
}

export default new AgencyAdminService();
